import React, {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import DashboardLayout from '@/shared/layout/DashboardLayout';
import PageState from '@/shared/components/PageState';
import ResultLoadingState from '@/shared/components/ResultLoadingState';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppDispatch, useAppSelector} from '@/shared/hooks/redux';
import {useTestsState, useTestsByName} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {
    restoreCurrentPatientFromStorage,
    selectCurrentPatient,
    selectSubPatients,
    setCurrentPatient,
} from '@/slices/patientSlice';
import SingleResultContent from './SingleResultContent';
import type {Test} from '@/shared/types/api';

const PatientSingleResult: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {user, token} = useAuth();
    const dispatch = useAppDispatch();

    const currentPatient = useAppSelector(selectCurrentPatient);
    const subPatients = useAppSelector(selectSubPatients);
    const testsState = useTestsState();
    const currentPatientData = useTestsByName(currentPatient?.fullName || '');

    // Restore current patient from localStorage on page load (handles token refresh scenarios)
    useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && user?.token) {
            dispatch(restoreCurrentPatientFromStorage());
        }
    }, [dispatch, currentPatient?.id, user?.token]);

    // Set current patient from subPatients data (extracted from test results)
    useEffect(() => {
        if (currentPatient?.id && currentPatient.id > 0) {
            return;
        }
        if (subPatients.length === 0) {
            return;
        }

        // Current patient (if any) is restored in the previous effect; if it's still missing,
        // default to the first valid patient extracted from the test results.
        const fallbackPatient = subPatients.find((p) => p.id > 0 && p.firstName && p.lastName);
        if (fallbackPatient) {
            dispatch(setCurrentPatient(fallbackPatient));
        }
    }, [dispatch, currentPatient?.id, subPatients]);

    // Find the specific test result
    let realResult: Test | null = null; // Keep real result for PDF download

    // Always find real result for PDF downloads
    for (let i = 0; i < currentPatientData.length; i++) {
        if (currentPatientData[i].id.toString() === id) {
            realResult = currentPatientData[i];
            break;
        }
    }

    // Always show the real result — lab results are never gated by membership.
    const result: Test | null = realResult;

    if (testsState.loading) {
        return (
            <DashboardLayout>
                <ResultLoadingState />
            </DashboardLayout>
        );
    }

    if (!result) {
        return (
            <DashboardLayout>
                <PageState
                    title="Result Not Found"
                    description="The requested test result could not be found."
                    primaryAction={{label: 'Back', onClick: () => navigate('/results')}}
                />
            </DashboardLayout>
        );
    }

    return (
        <SingleResultContent
            result={result}
            realResult={realResult}
            token={token}
            backTo="/results"
            backLabel="Back to Results"
        />
    );
};

export default PatientSingleResult;
